import { ReportProblemTwoTone } from '@mui/icons-material';
import { Box, Slide, Typography } from '@mui/material';
import { useContent } from 'contexts/Content/Content';
import { usePopups } from 'contexts/Popups/Popups';
import gsap from 'gsap';
import { parseHtml } from 'helpers';
import { useCallback, useEffect, useState } from 'react';

export default function PopupNotification() {
    const [current, setCurrent] = useState<PopupProps | null>(null);
    const [offset, setOffset] = useState(75);
    const [open, setOpen] = useState(false);
    const content = useContent();
    const popups = usePopups();

    const handleClose = useCallback(
        (e?: KeyboardEvent) => {
            if (popups.notification && (!e || e.key === 'Escape')) {
                gsap.killTweensOf(handleClose);
                popups.hide(popups.notification.name);
            }
        },
        [popups]
    );

    useEffect(() => {
        // Add listeners
        window.addEventListener('keydown', handleClose);

        return () => {
            // Remove listeners
            window.removeEventListener('keydown', handleClose);
        };
    }, [handleClose]);

    useEffect(() => {
        const newOffset = 75 + (content.general.portionMarking ? 16 : 0);
        setOffset(newOffset);
    }, [content.general.portionMarking]);

    useEffect(() => {
        if (!current && !open && popups.notification) {
            // Close after reading at 4 words per second
            const words = popups.notification.text.split(' ').length;
            const delay = (words / 4) * gsap.globalTimeline.timeScale();
            gsap.delayedCall(delay, handleClose);

            // Open popup
            setCurrent(popups.notification);
            setOpen(true);
        } else if (open && (!popups.notification || current?.name !== popups.notification?.name)) {
            setOpen(false);
        }
    }, [handleClose, current, open, popups.notification]);

    function handleClosed() {
        // Clear state
        setCurrent(null);
    }

    return (
        <Box left={500} overflow="hidden" position="fixed" right={16} top={offset} zIndex={1}>
            <Slide in={open} mountOnEnter unmountOnExit onExited={handleClosed}>
                <Box
                    alignItems="center"
                    bgcolor="white"
                    borderRadius={3}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    paddingBottom={2}
                    paddingTop={5}
                    paddingX={3}
                    position="relative"
                    role="alert"
                    sx={{ userSelect: 'none' }}
                    top={-24}
                >
                    {current?.icon === 'danger' && <ReportProblemTwoTone color="error" fontSize="large" />}
                    {current?.text && (
                        <Typography
                            align="center"
                            dangerouslySetInnerHTML={parseHtml(current.text)}
                            variant="roboto416"
                        />
                    )}
                </Box>
            </Slide>
        </Box>
    );
}
